import {SearchIcon, XCircleFillIcon} from '@primer/octicons-react'
import {SegmentedControl, TextInput} from '@primer/react'
import {type ChangeEvent, memo, useCallback, useDeferredValue, useState} from 'react'

import styles from './slicer-filter-controls.module.css'

export type SliceByFilterMode = 'include' | 'exclude'

/**
 * Returns true if the given display name should be visible given the search query and filter mode.
 * In 'include' mode, items matching the search are shown.
 * In 'exclude' mode, items matching the search are hidden.
 */
export function matchesSlicerSearch(
  displayName: string,
  searchQuery: string,
  mode: SliceByFilterMode = 'include',
): boolean {
  if (!searchQuery) return true
  const matches = displayName.toLowerCase().includes(searchQuery.toLowerCase())
  return mode === 'exclude' ? !matches : matches
}

type SlicerFilterControlsProps = {
  totalItemCount: number
  visibleItemCount: number
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  filterMode: SliceByFilterMode
  onFilterModeChange: (mode: SliceByFilterMode) => void
}

export const SlicerFilterControls = memo<SlicerFilterControlsProps>(function SlicerFilterControls({
  totalItemCount,
  visibleItemCount,
  searchQuery,
  onSearchQueryChange,
  filterMode,
  onFilterModeChange,
}) {
  const isFiltered = searchQuery.length > 0

  const handleModeChange = useCallback(
    (index: number) => {
      onFilterModeChange(index === 0 ? 'include' : 'exclude')
    },
    [onFilterModeChange],
  )

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onSearchQueryChange(e.target.value)
    },
    [onSearchQueryChange],
  )

  return (
    <div className={styles.filterContainer}>
      <TextInput
        aria-label="Filter values"
        leadingVisual={SearchIcon}
        placeholder="Filter values..."
        value={searchQuery}
        onChange={handleSearchChange}
        className={styles.searchInput}
        size="small"
        trailingAction={
          searchQuery ? (
            <TextInput.Action
              onClick={() => onSearchQueryChange('')}
              icon={XCircleFillIcon}
              aria-label="Clear search"
            />
          ) : undefined
        }
      />
      <div className={styles.filterControls}>
        <SegmentedControl size="small" aria-label="Filter mode" onChange={handleModeChange}>
          <SegmentedControl.Button selected={filterMode === 'include'}>Include</SegmentedControl.Button>
          <SegmentedControl.Button selected={filterMode === 'exclude'}>Exclude</SegmentedControl.Button>
        </SegmentedControl>
        {isFiltered && (
          <span className={styles.filterStatus} role="status">
            Showing {visibleItemCount} of {totalItemCount}
          </span>
        )}
      </div>
    </div>
  )
})

/**
 * Hook to manage the local search query state with deferred updates.
 * The deferred value is used for filtering to keep the input responsive.
 */
export function useSlicerSearch() {
  const [searchQuery, setSearchQuery] = useState('')
  const deferredSearchQuery = useDeferredValue(searchQuery)

  return {searchQuery, setSearchQuery, deferredSearchQuery}
}
